import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { useProject } from '../../context/ProjectContext';
import { useTeamContext } from '../../context/TeamContext';
import teamService from '../../services/teamService';
import DatePickerInput from '../Common/DatePickerInput';
import './ProjectComposer.css';

const toDate = (str) => str ? new Date(str + 'T00:00:00') : null;
const fmtDate = (date) => date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : '';

const ProjectComposer = ({ isOpen, onClose, onSuccess = onClose, project = null }) => {
    const { createProject, updateProject } = useProject();
    const { teams } = useTeamContext();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);

    const [formData, setFormData] = useState({
        team_id: '',
        title: '',
        description: '',
        project_manager_id: '',
        sro_id: '',
        ccio_id: '',
        status: 'not_started',
        start_date: '',
        end_date: '',
    });

    const [errors, setErrors] = useState({});

    const isEditing = !!project;

    // Fetch team members when team_id changes
    const fetchTeamMembers = useCallback(async (teamId) => {
        if (!teamId) {
            setTeamMembers([]);
            return;
        }

        try {
            setIsLoadingMembers(true);
            const response = await teamService.members(teamId);
            setTeamMembers(response?.data?.members || []);
        } catch (error) {
            console.error('Failed to fetch team members:', error);
            setTeamMembers([]);
        } finally {
            setIsLoadingMembers(false);
        }
    }, []);

    // Reset form when modal opens/closes or project changes
    useEffect(() => {
        if (isOpen) {
            if (project) {
                setFormData({
                    team_id: project.team_id || '',
                    title: project.title || '',
                    description: project.description || '',
                    project_manager_id: project.project_manager_id || '',
                    sro_id: project.sro_id || '',
                    ccio_id: project.ccio_id || '',
                    status: project.status || 'not_started',
                    start_date: project.start_date || '',
                    end_date: project.end_date || '',
                });
                // Fetch team members for editing mode
                if (project.team_id) {
                    fetchTeamMembers(project.team_id);
                }
            } else {
                // Use local date, not UTC to avoid timezone issues
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const today = `${year}-${month}-${day}`;

                setFormData({
                    team_id: '',
                    title: '',
                    description: '',
                    project_manager_id: '',
                    sro_id: '',
                    ccio_id: '',
                    status: 'not_started',
                    start_date: today,
                    end_date: '',
                });
                setTeamMembers([]);
            }
            setErrors({});
        }
    }, [isOpen, project, fetchTeamMembers]);

    // Handle team change - fetch members and reset user selections
    const handleTeamChange = async (e) => {
        const teamId = e.target.value;

        // Reset user selections when team changes
        setFormData(prev => ({
            ...prev,
            team_id: teamId,
            project_manager_id: '',
            sro_id: '',
            ccio_id: '',
        }));

        // Clear errors for user fields
        setErrors(prev => ({
            ...prev,
            team_id: null,
            project_manager_id: null,
            sro_id: null,
            ccio_id: null,
        }));

        // Fetch team members
        await fetchTeamMembers(teamId);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        try {
            setIsSubmitting(true);

            // Client-side validation: start_date must not be before today (use local date)
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const today = `${year}-${month}-${day}`;

            if (formData.start_date < today) {
                setErrors({ start_date: ['Start date cannot be before today'] });
                setIsSubmitting(false);
                return;
            }

            // Validate end_date is not before start_date
            if (formData.end_date < formData.start_date) {
                setErrors({ end_date: ['End date cannot be before start date'] });
                setIsSubmitting(false);
                return;
            }

            const data = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                project_manager_id: parseInt(formData.project_manager_id),
                sro_id: parseInt(formData.sro_id),
                ccio_id: parseInt(formData.ccio_id),
                status: formData.status,
                start_date: formData.start_date,
                end_date: formData.end_date,
            };

            if (isEditing) {
                await updateProject(project.id, data);
            } else {
                await createProject({
                    ...data,
                    team_id: parseInt(formData.team_id),
                });
            }

            onSuccess();
        } catch (error) {
            // Handle validation errors from API
            if (error?.status === 422) {
                setErrors(error.errors);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Handle ESC key
    useEffect(() => {
        if (isOpen) {
            const handleKeyDown = (e) => {
                if (e.key === 'Escape') {
                    onClose();
                }
            };
            window.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                document.body.style.overflow = '';
            };
        }
    }, [isOpen, onClose]);

    // Check if user dropdowns should be disabled
    const isUserDropdownDisabled = !formData.team_id || isLoadingMembers;

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="project-composer-overlay" onClick={handleBackdropClick}>
            <div className="project-composer-modal" role="dialog" aria-modal="true">
                {/* Header */}
                <div className="project-composer-header">
                    <h2 className="project-composer-title">
                        {isEditing ? 'Edit Project' : 'Create Project'}
                    </h2>
                    <button
                        type="button"
                        className="project-composer-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Body */}
                <div className="project-composer-body">
                    <form id="project-form" onSubmit={handleSubmit} noValidate>

                        {/* Project Team - MOVED TO TOP */}
                        <div className="project-form-group">
                            <label className="project-form-label">
                                Project Team:<span className="required">*</span>
                            </label>
                            <select
                                name="team_id"
                                value={formData.team_id}
                                onChange={handleTeamChange}
                                className={`project-form-select ${errors.team_id ? 'error' : ''}`}
                                disabled={isEditing}
                            >
                                <option value="">Select a team</option>
                                {teams.map(team => (
                                    <option key={team.id} value={team.id}>
                                        {team.name}
                                    </option>
                                ))}
                            </select>
                            {errors.team_id && <div className="project-form-error">{errors.team_id[0]}</div>}
                        </div>

                        {/* Project Name */}
                        <div className="project-form-group">
                            <label className="project-form-label">
                                Project Name:<span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className={`project-form-input ${errors.title ? 'error' : ''}`}
                                placeholder="Nova App"
                                autoFocus
                            />
                            {errors.title && <div className="project-form-error">{errors.title[0]}</div>}
                        </div>

                        {/* Project Description */}
                        <div className="project-form-group">
                            <label className="project-form-label">
                                Project Description:
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                className={`project-form-textarea ${errors.description ? 'error' : ''}`}
                                placeholder="Description..."
                            />
                            {errors.description && <div className="project-form-error">{errors.description[0]}</div>}
                        </div>

                        {/* Project Manager */}
                        <div className="project-form-group">
                            <label className="project-form-label">
                                Project Manager:<span className="required">*</span>
                            </label>
                            <select
                                name="project_manager_id"
                                value={formData.project_manager_id}
                                onChange={handleChange}
                                className={`project-form-select ${errors.project_manager_id ? 'error' : ''}`}
                                disabled={isUserDropdownDisabled}
                            >
                                <option value="">
                                    {isLoadingMembers ? 'Loading...' : 'Select manager'}
                                </option>
                                {teamMembers.map(member => (
                                    <option key={member.id} value={member.id}>
                                        {member.name}
                                    </option>
                                ))}
                            </select>
                            {errors.project_manager_id && <div className="project-form-error">{errors.project_manager_id[0]}</div>}
                        </div>

                        {/* SRO & CCIO */}
                        <div className="project-form-row">
                            <div className="project-form-group">
                                <label className="project-form-label">
                                    SRO:<span className="required">*</span>
                                </label>
                                <select
                                    name="sro_id"
                                    value={formData.sro_id}
                                    onChange={handleChange}
                                    className={`project-form-select ${errors.sro_id ? 'error' : ''}`}
                                    disabled={isUserDropdownDisabled}
                                >
                                    <option value="">
                                        {isLoadingMembers ? 'Loading...' : 'Select SRO'}
                                    </option>
                                    {teamMembers.map(member => (
                                        <option key={member.id} value={member.id}>
                                            {member.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.sro_id && <div className="project-form-error">{errors.sro_id[0]}</div>}
                            </div>

                            <div className="project-form-group">
                                <label className="project-form-label">
                                    CCIO:<span className="required">*</span>
                                </label>
                                <select
                                    name="ccio_id"
                                    value={formData.ccio_id}
                                    onChange={handleChange}
                                    className={`project-form-select ${errors.ccio_id ? 'error' : ''}`}
                                    disabled={isUserDropdownDisabled}
                                >
                                    <option value="">
                                        {isLoadingMembers ? 'Loading...' : 'Select CCIO'}
                                    </option>
                                    {teamMembers.map(member => (
                                        <option key={member.id} value={member.id}>
                                            {member.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.ccio_id && <div className="project-form-error">{errors.ccio_id[0]}</div>}
                            </div>
                        </div>

                        {/* Status, Start Date & End Date */}
                        <div className="project-form-row-3">
                            <div className="project-form-group">
                                <label className="project-form-label">
                                    Status:<span className="required">*</span>
                                </label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className={`project-form-select ${errors.status ? 'error' : ''}`}
                                >
                                    <option value="not_started">Not Started</option>
                                    <option value="project_initiation">Project Initiation</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="on_hold">On Hold</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                                {errors.status && <div className="project-form-error">{errors.status[0]}</div>}
                            </div>

                            <div className="project-form-group">
                                <label className="project-form-label">
                                    Start Date:<span className="required">*</span>
                                </label>
                                <DatePickerInput
                                    selected={toDate(formData.start_date)}
                                    onChange={(date) => {
                                        const newStartDate = fmtDate(date);
                                        setFormData(prev => ({
                                            ...prev,
                                            start_date: newStartDate,
                                            // Auto-fill end_date with start_date if end_date is empty
                                            end_date: prev.end_date || newStartDate
                                        }));
                                        if (errors.start_date) setErrors(prev => ({ ...prev, start_date: null }));
                                    }}
                                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                    placeholder="Select start date"
                                    isInvalid={!!errors.start_date}
                                />
                                {errors.start_date && <div className="project-form-error">{errors.start_date[0]}</div>}
                            </div>

                            <div className="project-form-group">
                                <label className="project-form-label">
                                    End Date:<span className="required">*</span>
                                </label>
                                <DatePickerInput
                                    selected={toDate(formData.end_date)}
                                    onChange={(date) => {
                                        setFormData(prev => ({ ...prev, end_date: fmtDate(date) }));
                                        if (errors.end_date) setErrors(prev => ({ ...prev, end_date: null }));
                                    }}
                                    minDate={toDate(formData.start_date) || new Date(new Date().setHours(0, 0, 0, 0))}
                                    placeholder="Select end date"
                                    isInvalid={!!errors.end_date}
                                />
                                {errors.end_date && <div className="project-form-error">{errors.end_date[0]}</div>}
                            </div>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="project-composer-footer">
                    <button
                        type="button"
                        className="project-composer-btn project-composer-btn-secondary"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="project-form"
                        className="project-composer-btn project-composer-btn-primary"
                        disabled={isSubmitting}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border" role="status" aria-hidden="true"></span>
                                <span>Saving...</span>
                            </>
                        ) : 'Save'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ProjectComposer;
